import React, { memo, useCallback, useMemo, useState } from 'react';
import {
  Checkbox,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  MenuItem,
  TextField,
} from '@mui/material';
import { t } from 'i18next';
import { useFormik } from 'formik';
import * as yup from 'yup';
import useFormSubmit from 'hooks/useFormSubmit';
import { useAppInfo } from 'context';
import FormActionButtons from 'components/ui/form/FormActionButtons';
// import FormSelectField from 'components/ui/form/FormSelectField';
import { useQuery } from 'react-query';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import { colors } from 'shared/colors';

const validationSchema = yup.object({
  staff_ids: yup
    .array()
    .of(yup.string())
    .min(1, 'change.tab.contract.validation.staffId')
    .required('Tags maydoni majburiy'),
});

export default memo(function DistributionLeads({ open, setOpen, leads }) {
  const [{ refetchTaskCount }] = useAppInfo();
  const { submit, isSubmitting } = useFormSubmit();
  const axiosPrivate = useAxiosPrivate();
  const [searchTxt, setSearchTxt] = useState('');
  const formik = useFormik({
    initialValues: {
      staff_ids: [],
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      if (leads?.length) {
        const body = { ...values, leads };
        submit(
          { type: 'put', contentType: 'simple' },
          body,
          '/crm/crm-lead/distribution-leads',
          'Lidlar taqsimlandi',
          null,
          true,
          onRefetch,
        );
      }
    },
  });

  const onRefetch = () => {
    refetchTaskCount?.();
    setOpen(null);
    formik.resetForm();
  };

  const { data: staffs, isLoading } = useQuery({
    queryKey: ['/dictionary/staffes'],
    queryFn: async function () {
      const response = await axiosPrivate.get('/dictionary/staffes');
      return response?.data?.data || [];
    },
    onError: (error) => {},
    retry: false,
  });

  const onClose = () => {
    setOpen(null);
    formik.resetForm();
  };

  const getHighlightedValue = (value) => {
    return value && value.length > 0
      ? value.replaceAll(
          searchTxt,
          `<span class="bg-base-color-disabled text-white">${searchTxt}</span>`,
        )
      : '';
  };

  const filteredStaffs = useMemo(() => {
    if (!staffs?.length) return [];
    return (
      staffs?.filter((st) =>
        st?.name?.toUpperCase()?.includes(searchTxt?.toUpperCase()),
      ) || []
    );
  }, [staffs, searchTxt]);

  const onChange = useCallback(
    (e) => {
      const { checked, value } = e?.target;
      const ids = formik.values?.staff_ids || [];
      if (checked) {
        formik.setFieldValue('staff_ids', [...ids, value]);
      } else {
        const newIds = ids?.filter((id) => id != value);
        formik.setFieldValue('staff_ids', newIds);
      }
    },
    [formik.values.staff_ids],
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth='lg'
      PaperProps={{ className: '!base-border !shadow-none !overflow-hidden' }}
    >
      <DialogTitle
        id='alert-dialog-title'
        className='!bg-[#CDCDCDC7] !text-center !border-b !border-b-[#E6E6E6]'
      >
        <span className='pr-5 font-semibold text-[21px] text-[#374957]'>
          {t('tasks.distributionLeads.title')}
        </span>
      </DialogTitle>
      <DialogContent>
        <div className='py-4 px-8 min-w-[600px]'>
          <form onSubmit={formik.handleSubmit}>
            <div className='flex gap-2 items-center'>
              <div className='rounded-full w-12 h-12 bg-blue-50 flex  items-center justify-center'>
                <div className='rounded-full w-8 h-8 bg-blue-100 flex  items-center justify-center'>
                  <i className='bi bi-exclamation-circle text-lg text-blue-600'></i>
                </div>
              </div>

              <span>{t('tasks.distributionLeads.description')}</span>
            </div>
            <div className='flex justify-center flex-col gap-4'>
              <div className='crm-lead-search-wrapper w-full'>
                <TextField
                  color='secondary'
                  variant='outlined'
                  fullWidth
                  id='crm-search-staff-field'
                  name='crm-search-staff-field'
                  label={t('common.global.search')}
                  value={searchTxt}
                  onChange={(event) => setSearchTxt(event.target.value)}
                  autoComplete='off'
                  className='h-[40px]'
                  InputProps={{
                    endAdornment: (
                      <InputAdornment
                        position='end'
                        className='custom-endAdornment'
                      >
                        {searchTxt ? (
                          <IconButton
                            onClick={() => {
                              setSearchTxt('');
                            }}
                            variant='onlyIcon'
                          >
                            <i className='bi bi-x end-adornment-close-button' />
                          </IconButton>
                        ) : (
                          <IconButton variant='onlyIcon' disabled disableRipple>
                            <i className='bi bi-search text-base leading-4' />
                          </IconButton>
                        )}
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root.Mui-focused i': {
                      color: colors.baseColorLight,
                    },
                    '& .MuiOutlinedInput-root i': {
                      color: colors.grayColor,
                    },
                  }}
                />

                <div className='!w-full max-h-96 my-scroll overflow-y-auto !z-[9999] !bg-white !shadow-md !top-2'>
                  {isLoading ? (
                    <div className='flex items-center justify-center my-[14px]'>
                      <CircularProgress size={20} color='inherit' />
                    </div>
                  ) : filteredStaffs?.length > 0 ? (
                    filteredStaffs?.map((item, index) => (
                      <div
                        className='column-card no-underline'
                        key={`search-result-item-${index}`}
                      >
                        <MenuItem>
                          <label className='text-gray-900 flex gap-1 items-center text-sm leading-5 w-full'>
                            <Checkbox
                              size='small'
                              checked={formik?.values?.staff_ids?.includes(
                                `${item?.id}`,
                              )}
                              value={item?.id}
                              onChange={onChange}
                            />
                            <div
                              dangerouslySetInnerHTML={{
                                __html: getHighlightedValue(item?.name),
                              }}
                            ></div>
                            {item?.contacts &&
                              item?.contacts.length > 0 &&
                              item?.contacts.map((contact, contactInd) => (
                                <div
                                  key={`item-contact-${contactInd}`}
                                  className='text-gray-700 text-[12px] whitespace-normal leading-4'
                                >
                                  {contact?.phones &&
                                    contact?.phones.length > 0 &&
                                    contact?.phones.map((phone, phoneInd) => (
                                      <div
                                        key={`contact-${contactInd}-phone-${phoneInd}`}
                                        className='ml-2'
                                        dangerouslySetInnerHTML={{
                                          __html: getHighlightedValue(
                                            phone?.phone,
                                          ),
                                        }}
                                      />
                                    ))}
                                  <div
                                    className='ml-2'
                                    dangerouslySetInnerHTML={{
                                      __html: getHighlightedValue(
                                        contact?.name,
                                      ),
                                    }}
                                  />
                                </div>
                              ))}
                          </label>
                        </MenuItem>
                      </div>
                    ))
                  ) : (
                    <div>
                      <span className='no-data-found-wrapper select-box my-2'>
                        <i className='bi bi-exclamation-octagon text-lg mr-1' />{' '}
                        {t('common.global.noDataFound')}
                      </span>
                    </div>
                  )}
                </div>
              </div>
              <div className='flex justify-between items-center'>
                <div>
                  {t('sidebar.admin.lead')}: {leads?.length}
                </div>
                <FormActionButtons
                  delay={0}
                  isSubmitting={isSubmitting}
                  disabled={!leads?.length || !formik.values?.staff_ids?.length}
                  formType='dialog'
                  setOpen={setOpen}
                  reset={formik.resetForm}
                />
              </div>
            </div>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
});
